// Window placement validation.
//
// Windows are the one furniture class that lives on a wall rather than on the
// floor, so "can this go here?" is a different question from ordinary depth /
// footprint math — it is about wall flushness and window-vs-window overlap.
// Kept out of depthMath.ts so that file stays about depth only.
import { readFurnitureData, type SceneNode, type SceneNodeChild } from './sceneNode';
import type { RenderFurniture } from './types';
import { isWallMountedItem, getTileDims, getWindowWallSide } from './tileMath';
import { snapWindowAnchorToGrid, WINDOW_VERTICAL_OFFSET, wallItemHeightPx, wallVerticalSpan } from './windowSnap';
import { getFurnitureDepthComponents } from './depthMath';


// ── Window placement validation ──
// Check whether a window at the given base-tile rectangle is valid:
//   1. It must be flush against a wall (baseTileX=0 or baseTileY=0).
//   2. It must not overlap any other placed window — along the wall AND, when the
//      caller gives its vertical extent, in height (wall items hang at any height,
//      so two paintings one above the other are fine).
// Returns { valid, wallSide } so callers can also auto-mirror.
export function validateWindowPlacement(
  markers: Map<number, SceneNode>,
  baseTileX: number,
  baseTileY: number,
  overlayW: number,
  overlayH: number,
  excludeFurnitureId: number,
  /** Height on the wall (wallVerticalSpan) of the item being placed. */
  vertical?: { bottom: number; top: number },
): { valid: boolean; wallSide: 'left' | 'right' | null } {
  const wallSide = getWindowWallSide(baseTileX, baseTileY);
  if (!wallSide) return { valid: false, wallSide: null };

  // Check overlap with other placed windows/wall-mounted
  for (const [fId, marker] of markers.entries()) {
    if (fId === excludeFurnitureId) continue;
    const sp = marker.children[0] as SceneNodeChild | undefined;
    const fData = readFurnitureData(marker, sp);
    if (!fData || !isWallMountedItem(fData.furniture_catalog)) continue;

    const { w, h, isRug: fIsRug } = getTileDims(fData.furniture_catalog);
    // For windows, temporarily subtract vertical offset to calculate correct tile position
    const originalOtherY = marker.position.y;
    marker.position.y = originalOtherY - WINDOW_VERTICAL_OFFSET;
    const otherDepth = getFurnitureDepthComponents(marker, w, h, fIsRug);
    marker.position.y = originalOtherY; // Restore

    // Use snap to get correct wall-flush baseTile for other windows. Their marker
    // also carries their own height on the wall (wall_floor_offset) — take it out.
    const otherOffset = fData.wall_floor_offset ?? fData.furniture_catalog?.wall_floor_offset ?? 0;
    const otherFloorY = originalOtherY - WINDOW_VERTICAL_OFFSET - otherOffset;
    const otherSnap = snapWindowAnchorToGrid(
      marker.position.x, otherFloorY,
      otherDepth.effectiveWidthTiles,
      otherDepth.effectiveHeightTiles,
    );
    const otherIsMir = !!fData.is_mirrored;
    const otherOverlayW = otherIsMir ? otherDepth.effectiveHeightTiles : otherDepth.effectiveWidthTiles;
    const otherOverlayH = otherIsMir ? otherDepth.effectiveWidthTiles : otherDepth.effectiveHeightTiles;

    // AABB overlap test
    const ax1 = baseTileX, ax2 = baseTileX + overlayW;
    const ay1 = baseTileY, ay2 = baseTileY + overlayH;
    const bx1 = otherSnap.baseTileX, bx2 = otherSnap.baseTileX + otherOverlayW;
    const by1 = otherSnap.baseTileY, by2 = otherSnap.baseTileY + otherOverlayH;
    if (ax1 < bx2 && ax2 > bx1 && ay1 < by2 && ay2 > by1) {
      if (vertical && sp) {
        // Heights above the wall base — screen y would differ along the slanted wall.
        const other = wallVerticalSpan(otherOffset, otherDepth.effectiveHeightTiles, wallItemHeightPx(sp.width, sp.height));
        if (vertical.top <= other.bottom || vertical.bottom >= other.top) continue;
      }
      return { valid: false, wallSide };
    }
  }
  return { valid: true, wallSide };
}

/**
 * Whether a placed wall item sits on a valid spot (flush on a wall, no overlap) —
 * from its marker as rendered (which carries WINDOW_VERTICAL_OFFSET and its own
 * wall_floor_offset).
 */
export function isPlacedWallItemValid(
  markers: Map<number, SceneNode>,
  marker: SceneNode,
  fData: RenderFurniture,
  sprite: SceneNodeChild,
): boolean {
  const { w, h, isRug } = getTileDims(fData.furniture_catalog);
  const markerY = marker.position.y;
  marker.position.y = markerY - WINDOW_VERTICAL_OFFSET;
  const depth = getFurnitureDepthComponents(marker, w, h, isRug);
  marker.position.y = markerY;
  const isMir = !!fData.is_mirrored;
  const ownOffset = fData.wall_floor_offset ?? fData.furniture_catalog?.wall_floor_offset ?? 0;
  const snap = snapWindowAnchorToGrid(
    marker.position.x, markerY - WINDOW_VERTICAL_OFFSET - ownOffset,
    depth.effectiveWidthTiles,
    depth.effectiveHeightTiles,
  );
  const overlayW = isMir ? depth.effectiveHeightTiles : depth.effectiveWidthTiles;
  const overlayH = isMir ? depth.effectiveWidthTiles : depth.effectiveHeightTiles;
  return validateWindowPlacement(
    markers,
    snap.baseTileX, snap.baseTileY,
    overlayW, overlayH,
    fData.id,
    wallVerticalSpan(ownOffset, depth.effectiveHeightTiles, wallItemHeightPx(sprite.width, sprite.height)),
  ).valid;
}
